import Image from 'app/Components/Shared/Image/image';
import React, {useState, useEffect, useRef} from 'react';
import BtnSubmit from 'app/Components/Shared/Buttons/BtnSubmit';
import ReactSelect from 'app/Components/Shared/ReactSelect/ReactSelect';
import CheckBox from 'app/Components/Shared/CheckBox/Checkbox';
import TextInput from 'app/Components/Shared/TextInput/TextInput';
import BtnBlackBorder from 'app/Components/Shared/Buttons/BtnWhiteBlackBorderSmallSize';
import BtnWhiteBlackBorder from 'app/Components/Shared/Buttons/BtnWhiteBlackBorderSmallSize';
import DatePicker from 'app/Components/Shared/DatePicker/DatePicker';
import InputWithActions from 'app/Components/Shared/InputWithActions/InputWithActions';
import {parse} from 'date-fns';
import {isMobile} from 'react-device-detect';

import './FilterBar.scss';

const FilterBar = ({filterOptions}) => {
  const filters = filterOptions.tableFilters.fields;
  const [filterValues, setFilterValues] = useState({});
  const [validationError, setValidationError] = useState('');
  const [mobileFilterValues, setMobileFilterValues] = useState({});
  const [showMobileFilterPopup, setShowMobileFilterPopup] = useState(false);

  // Initialize default values when component mounts
  useEffect(() => {
    setDefaultFilterValues();
  }, []);

  function setDefaultFilterValues() {
    if (filters && filters.length > 0) {
      filters.forEach((filter) => {
        if (filter.defaultValue && typeof filter.defaultValue === 'object' && Object.keys(filter.defaultValue).length > 0) {
          Object.entries(filter.defaultValue).forEach(([key, value]) => {
            setFilterValues((prevFilterValues) => ({
              ...prevFilterValues,
              [key]: value,
            }));
          });
        }
      });
    }
  }

  const handleFilterChange = (key, value) => {
    setFilterValues((prevFilterValues) => {
      const newValues = {
        ...prevFilterValues,
        [key]: value,
      };

      // Call the callback inside the setter with the updated values
      if (filterOptions.onFilterChange) {
        filterOptions.onFilterChange(key, value, newValues);
      }

      return newValues;
    });

    // Clear validation error when user makes changes
    if (validationError) {
      setValidationError('');
    }
  };

  // Helper function for date validation
  const validateDateRange = () => {
    const hasStartDate = filterValues.startDate && filterValues.startDate.trim() !== '';
    const hasEndDate = filterValues.endDate && filterValues.endDate.trim() !== '';

    // Both dates must be provided or both empty
    if (hasStartDate && !hasEndDate) {
      return 'יש לבחור גם תאריך סיום';
    }

    if (hasEndDate && !hasStartDate) {
      return 'יש לבחור גם תאריך התחלה';
    }

    // Ensure start date is not after end date
    if (hasStartDate && hasEndDate) {
      const toDate = (val) => {
        if (val instanceof Date) return val;
        try {
          return parse(val, 'dd/MM/yyyy', new Date());
        } catch {
          return null;
        }
      };

      const startDate = toDate(filterValues.startDate);
      const endDate = toDate(filterValues.endDate);

      if (startDate && endDate && startDate > endDate) {
        return 'תאריך ההתחלה חייב להיות לפני תאריך הסיום';
      }
    }

    return null; // No validation errors
  };

  const dateRangeChange = (key, date) => {
    const toDate = (val) => {
      if (val instanceof Date) return val;
      try {
        return parse(val, 'dd/MM/yyyy', new Date());
      } catch {
        return null;
      }
    };
    if (
      (key === 'startDate' && filterValues.endDate && toDate(date) > toDate(filterValues.endDate)) ||
      (key === 'endDate' && filterValues.startDate && toDate(date) < toDate(filterValues.startDate))
    ) {
      return;
    } else {
      handleFilterChange(key, date);
    }
  };

  const handleSearching = () => {
    if (showMobileFilterPopup) {
      setShowMobileFilterPopup(false);
    }
    if (filterOptions.submitSearching) {
      // Clear any previous validation errors
      setValidationError('');

      // Validate the form
      const error = validateDateRange();
      if (error) {
        setValidationError(error);
        return;
      }

      filterOptions.submitSearching(filterValues);
    }
  };

  const handleClearFilters = () => {
    // Reset to default values instead of completely clearing
    setFilterValues({});
    setDefaultFilterValues();

    if (filterOptions.onClearFilters) {
      filterOptions.onClearFilters();
    }
  };

  function getSelectBoxValue(filter) {
    if (filter.value && !filterValues[filter.key]) {
      setFilterValues((prevFilterValues) => ({
        ...prevFilterValues,
        [filter.key]: filter.value,
      }));
      return filter.options.filter((option) => option.key == filterValues[filter.value]);
    }
    return filter.options.filter((option) => option.key == filterValues[filter.key]);
  }

  const renderFilterField = (filter) => {
    const value = filterValues[filter.key] || filter.defaultValue || '';

    switch (filter.type) {
      case 'text':
        return (
          <TextInput
            value={value}
            id='FilterBarTextInput'
            placeholder={filter.placeholder}
            onChange={(e) => handleFilterChange(filter.key, e.target.value)}
          />
        );
      case 'textWithActions':
        return (
          <InputWithActions
            onChange={(e) => handleFilterChange(filter.key, e.target.value)}
            value={value}
            placeholder={filter.placeholder}
            actionText={filter.actionText}
            actionDisabled={false}
            onAction={handleSearching}
            onClear={() => {
              handleFilterChange(filter.key, '');
              setShowMobileFilterPopup(false);
              if (filter.onClear) {
                filter.onClear(filterValues);
              }
            }}
          />
        );

      case 'select':
        return (
          <ReactSelect
            getOptionLabel={(option) => option.value}
            getOptionValue={(option) => option.key}
            placeholder={filter.placeholder}
            value={getSelectBoxValue(filter)}
            name='filter-bar-select-box'
            options={filter.options}
            onChange={(e) => handleFilterChange(filter.key, e.key)}
          />
        );

      case 'date':
        return (
          <label className='has-float-label-required'>
            <DatePicker
              selectedDate={filterValues.startDate}
              setSelectedDate={(date) => handleFilterChange(filter.key, date)}
              numOfYearsBack={filter.numOfYearsBack}
            />
            <span className='float-label'>בחר תאריך</span>
          </label>
        );

      case 'dateRange':
        return (
          <div className='date-range-container'>
            <div className='date-range-input'>
              <label className='has-float-label-required'>
                <DatePicker
                  maxDate={filterValues.endDate || filter.maxDate}
                  dependedDate={filter.minDate || ''}
                  numOfYearsBack={filter.numOfYearsBack}
                  selectedDate={filterValues.startDate || filter.defaultValue.startDate}
                  setSelectedDate={(date) => dateRangeChange('startDate', date)}
                />
                <span className='float-label'> מתאריך</span>
              </label>
            </div>
            <div className='date-range-input'>
              <label className='has-float-label-required'>
                <DatePicker
                  maxDate={filter.maxDate}
                  dependedDate={filterValues.startDate || filter.minDate}
                  numOfYearsBack={filter.numOfYearsBack}
                  selectedDate={filterValues.endDate || filter.defaultValue.endDate}
                  setSelectedDate={(date) => dateRangeChange('endDate', date)}
                />
                <span className='float-label'> לתאריך</span>
              </label>
            </div>
          </div>
        );

      case 'checkbox':
        return (
          <div className='form-check'>
            <CheckBox
              id={'filter-bar-check-box'}
              onCheck={(e) => handleFilterChange(filter.key, e.target.checked)}
              onUncheck={(e) => handleFilterChange(filter.key, e.target.checked)}
              checked={!!value}
              label={filter.label}
            />
          </div>
        );
      case 'custom':
        return filter.render ? filter.render(filterValues) : null;

      default:
        return null;
    }
  };

  if (!filters || filters.length === 0) {
    return null;
  }

  // Generate dynamic classes based on what's shown
  const getWrapperClasses = () => {
    const classes = ['filter-bar-content-wrapper'];

    if (filterOptions?.tableFilters?.showClearButton) classes.push('has-clear-button');
    if (filterOptions.submitSearching) classes.push('has-action-button');

    return classes.join(' ');
  };

  const getMobileFilterBar = () => {
    if (!filterOptions.tableFilters || !isMobile) return null;

    return (
      <div className='mobile-filter-bar filter-bar-content'>
        <div className='mobile-filter-content'>
          {/* Extra button (like "פניה חדשה") */}
          {filterOptions.renderExtraButton && <div className='extra-button-container'>{filterOptions.renderExtraButton()}</div>}

          {/* Main filter fields */}
          <div className='main-filter-container'>
            <div className='main-filter-field filter-field'>
              {filters.map((filter) => (
                <div key={filter.key} className={`filter-field`} style={{width: filter.width}}>
                  {filter.label && <label className='filter-label'>{filter.label}</label>}
                  <div className='filter-input'>{renderFilterField(filter)}</div>
                </div>
              ))}
            </div>
            {/* Magnifying glass button */}
            <button className='mobile-filter-search-btn' onClick={() => setShowMobileFilterPopup(true)}>
              <Image src={require('./Icon/search.svg')} alt='search' />
            </button>
          </div>
        </div>
      </div>
    );
  };

  const getDesktopFilterBar = () => {
    return (
      <div className={getWrapperClasses()}>
        <div className='filter-bar-content content-left'>
          <Image src={require('./Icon/filter.svg')} />
          בחר מסננים:
        </div>
        <div className='filter-bar-content content-right'>
          {filters.map((filter) => (
            <div key={filter.key} className={`filter-field`} style={{width: filter.width}}>
              {filter.label && <label className='filter-label'>{filter.label}</label>}
              <div className='filter-input'>{renderFilterField(filter)}</div>
            </div>
          ))}
        </div>
        {filterOptions?.tableFilters?.showClearButton && (
          <div className='filter-bar-content content-clear'>
            <div className='filter-actions'>
              <a className='link' onClick={handleClearFilters}>
                איפוס
              </a>
            </div>
          </div>
        )}
        {filterOptions.submitSearching && (
          <div className='filter-bar-content content-action'>
            <div className='filter-action-button'>
              <BtnWhiteBlackBorder onclick={() => handleSearching()} text={' סנן'} />
              {validationError && (
                <div className='validation-error' style={{color: 'red', fontSize: '12px', marginTop: '4px'}}>
                  {validationError}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };
  const renderMobileFilterPopup = () => {
    if (!showMobileFilterPopup || !filterOptions.tableFilters?.popupFields) return null;

    return (
      <div className='mobile-filter-popup-content'>
        <div className='popup-content'>
          <div className='filter-bar-content'>
            {filterOptions?.tableFilters?.popupFields?.map((filter) => (
              <div key={filter.key} className={`filter-field`} style={{width: filter.width}}>
                {filter.label && <label className='filter-label'>{filter.label}</label>}
                <div className='filter-input'>{renderFilterField(filter)}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className='filter-bar'>
      <>{isMobile ? getMobileFilterBar() : getDesktopFilterBar()}</>
      <>{renderMobileFilterPopup()}</>
    </div>
  );
};

export default FilterBar;
